/**
 * Action Queue with Undo/Redo support.
 * Implements the Command pattern for reversible operations.
 */

// Action types
export const ActionType = {
  ADD_ENTITY: 'ADD_ENTITY',
  DELETE_ENTITY: 'DELETE_ENTITY',
  MOVE_ENTITY: 'MOVE_ENTITY',
  UPDATE_ENTITY_META: 'UPDATE_ENTITY_META',
  ROTATE_ENTITY: 'ROTATE_ENTITY',
  CLEAR_LAYOUT: 'CLEAR_LAYOUT',
};

// Maximum undo history
const MAX_HISTORY = 50;

class ActionQueue {
  constructor() {
    this.undoStack = [];
    this.redoStack = [];
    this.listeners = new Set();
  }

  /**
   * Subscribe to action queue changes.
   * @param {Function} listener - Called when undo/redo state changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  _notify() {
    for (const listener of this.listeners) {
      listener({
        canUndo: this.canUndo(),
        canRedo: this.canRedo(),
        undoCount: this.undoStack.length,
        redoCount: this.redoStack.length,
      });
    }
  }

  /**
   * Execute an action and add it to the undo stack.
   * @param {Object} action - { type, payload, store }
   */
  execute(action) {
    const { type, payload, store } = action;
    
    // Create the action record with forward and reverse operations
    const actionRecord = this._createActionRecord(type, payload, store);
    if (!actionRecord) return;

    // Execute the forward action
    actionRecord.execute();

    // Add to undo stack
    this.undoStack.push(actionRecord);
    
    // Limit stack size
    if (this.undoStack.length > MAX_HISTORY) {
      this.undoStack.shift();
    }

    // Clear redo stack on new action
    this.redoStack = [];
    
    this._notify();
  }

  /**
   * Undo the last action.
   */
  undo() {
    if (!this.canUndo()) return;

    const action = this.undoStack.pop();
    action.undo();
    this.redoStack.push(action);
    
    this._notify();
  }

  /**
   * Redo the last undone action.
   */
  redo() {
    if (!this.canRedo()) return;

    const action = this.redoStack.pop();
    action.execute();
    this.undoStack.push(action);
    
    this._notify();
  }

  canUndo() {
    return this.undoStack.length > 0;
  }

  canRedo() {
    return this.redoStack.length > 0;
  }

  /**
   * Clear all history.
   */
  clear() {
    this.undoStack = [];
    this.redoStack = [];
    this._notify();
  }

  /**
   * Create an action record with execute and undo functions.
   */
  _createActionRecord(type, payload, store) {
    switch (type) {
      case ActionType.ADD_ENTITY: {
        const { assetKey, tx, ty, options } = payload;
        let entityId = null;
        let entitySnapshot = null;
        
        return {
          type,
          execute: () => {
            const state = store.getState();
            const id = state._addEntityDirect(assetKey, tx, ty, options);
            entityId = id;
            entitySnapshot = state.layout.entities.find(e => e.id === id);
          },
          undo: () => {
            if (entityId) {
              store.getState()._deleteEntityDirect(entityId);
            }
          },
        };
      }

      case ActionType.DELETE_ENTITY: {
        const { id } = payload;
        let entitySnapshot = null;
        
        return {
          type,
          execute: () => {
            // Capture entity before deleting
            entitySnapshot = store.getState().layout.entities.find(e => e.id === id);
            store.getState()._deleteEntityDirect(id);
          },
          undo: () => {
            if (entitySnapshot) {
              store.getState()._restoreEntity(entitySnapshot);
            }
          },
        };
      }

      case ActionType.MOVE_ENTITY: {
        const { id, tx, ty } = payload;
        let prevTx = 0;
        let prevTy = 0;
        
        return {
          type,
          execute: () => {
            const entity = store.getState().layout.entities.find(e => e.id === id);
            if (entity) {
              prevTx = entity.tx;
              prevTy = entity.ty;
            }
            store.getState()._updateEntityPosDirect(id, tx, ty);
          },
          undo: () => {
            store.getState()._updateEntityPosDirect(id, prevTx, prevTy);
          },
        };
      }

      case ActionType.UPDATE_ENTITY_META: {
        const { id, meta } = payload;
        let prevMeta = null;
        
        return {
          type,
          execute: () => {
            const entity = store.getState().layout.entities.find(e => e.id === id);
            if (entity) {
              prevMeta = { ...entity.meta };
            }
            store.getState()._updateEntityMetaDirect(id, meta);
          },
          undo: () => {
            if (prevMeta !== null) {
              store.getState()._setEntityMeta(id, prevMeta);
            }
          },
        };
      }

      case ActionType.ROTATE_ENTITY: {
        const { id, direction } = payload;
        
        return {
          type,
          execute: () => {
            store.getState()._rotateFacingDirect(id, direction);
          },
          undo: () => {
            // Rotate in opposite direction
            const oppositeDir = direction === 'cw' ? 'ccw' : 'cw';
            store.getState()._rotateFacingDirect(id, oppositeDir);
          },
        };
      }

      case ActionType.CLEAR_LAYOUT: {
        let entitiesSnapshot = [];
        
        return {
          type,
          execute: () => {
            entitiesSnapshot = [...store.getState().layout.entities];
            store.getState()._clearLayoutDirect();
          },
          undo: () => {
            store.getState()._restoreEntities(entitiesSnapshot);
          },
        };
      }

      default:
        console.warn('Unknown action type:', type);
        return null;
    }
  }
}

// Singleton instance
export const actionQueue = new ActionQueue();

// React hook for subscribing to action queue state
export function useActionQueue() {
  const { useState, useEffect } = require('react');
  const [state, setState] = useState({
    canUndo: actionQueue.canUndo(),
    canRedo: actionQueue.canRedo(),
    undoCount: actionQueue.undoStack.length,
    redoCount: actionQueue.redoStack.length,
  });

  useEffect(() => {
    return actionQueue.subscribe(setState);
  }, []);

  return {
    ...state,
    undo: () => actionQueue.undo(),
    redo: () => actionQueue.redo(),
    clear: () => actionQueue.clear(),
  };
}
